import { useState, useEffect, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { respondToApproval, getPendingApprovals, markApprovalExpired } from "../../api/approvalApi";
import { scrollToBottom } from "../../utils/scrollHelpers";
import logger from "../../services/Logger";
import { STALE_NEVER } from "../../constants/const";
import QUERYKEYS from "../../api/queryKeys";
import useGenerateApiSignal from "../../store/useGenerateApiSignal";
import useStreamingStateStore from "../../store/useStreamingStateStore";

const removeApprovalGroup = (approvals, approvalId) => {
  const approvalGroupId = approvals[approvalId]?.approval_group_id;
  if (!approvalGroupId) {
    const { [approvalId]: removed, ...rest } = approvals;
    return rest;
  }
  return Object.fromEntries(
    Object.entries(approvals).filter(
      ([, approval]) => approval.approval_group_id !== approvalGroupId
    )
  );
};

/**
 * Manages approval card flow for agent actions requiring user confirmation.
 * Handles approval state, user interactions (confirm/cancel), expiration, and rehydration.
 * 
 * @param {string|undefined} chat_id - Current chat ID
 * @param {object} user - User object with uid
 * @param {object} loadingHandlers - Loading state handlers from createMessageLoadingHandlers
 * @param {object} queryClient - React Query client for cache operations
 * @param {string|undefined} spaceId - Space ID for cache key alignment (undefined for regular chats)
 * @returns {object} Approval state and handlers
 */
export const useApprovalFlow = (chat_id, user, loadingHandlers = null, queryClient = null, spaceId = undefined) => {
  const [pendingApprovals, setPendingApprovals] = useState({});
  const pendingApprovalsRef = useRef(pendingApprovals);
  
  // Keep ref in sync with state for async operations
  useEffect(() => {
    pendingApprovalsRef.current = pendingApprovals;
  }, [pendingApprovals]);
  
  // Fetch pending approvals using React Query
  const { data: pendingApprovalsData } = useQuery({
    queryKey: [QUERYKEYS.PENDING_APPROVALS, chat_id],
    queryFn: () => {
      return getPendingApprovals(chat_id);
    },
    enabled: !!(chat_id && user?.uid),
    refetchInterval: false, // Don't poll - rely on realtime events
    staleTime: STALE_NEVER, // Always consider stale so invalidation triggers refetch
  });
  
  // Update local state when React Query data changes
  useEffect(() => {
    if (!chat_id || !user?.uid) {
      setPendingApprovals({});
      return;
    }
    
    if (pendingApprovalsData && pendingApprovalsData.length > 0) {
      // Convert array to object keyed by approval_id
      const approvalsMap = {};
      for (const approval of pendingApprovalsData) {
        // Convert ISO string expires_at to Unix timestamp (seconds)
        let expiresAtTimestamp = approval.expires_at;
        if (typeof approval.expires_at === 'string') {
          const dateObj = new Date(approval.expires_at);
          expiresAtTimestamp = Math.floor(dateObj.getTime() / 1000);
        }
        
        approvalsMap[approval.approval_id] = {
          approval_id: approval.approval_id,
          agent_id: approval.intent_metadata?.agent_id,
          agent_name: approval.intent_metadata?.agent_name,
          action: approval.intent_metadata?.action,
          action_description: approval.intent_metadata?.reasoning,
          function_call_name: approval.intent_metadata?.function_call_name,
          function_call_args: approval.intent_metadata?.function_call_args,
          risk_level: approval.approval_level,
          confirmation_text: approval.expected_responses?.[0] || "confirm",
          expires_at: expiresAtTimestamp,
          message_id: approval.intent_metadata?.prompt_id,
          answer_id: approval.intent_metadata?.answer_id, // For optimistic cache update
          model_id: approval.intent_metadata?.model_id, // For showing model badge during streaming
          approval_group_id: approval.approval_group_id,
          approval_group_member_ids: approval.approval_group_member_ids,
          status: "pending",
          completedAt: null,
        };
      }
      setPendingApprovals(approvalsMap);

      // Rehydrate approval flags on the messages cache so handleApprovalConfirm can
      // find the right answer by approval_id after a page refresh or cross-device load.
      // (handleApprovalCardReceived sets these optimistically during live generation,
      //  but they're lost on refresh since they aren't persisted to Firestore answers.)
      if (queryClient) {
        for (const approval of pendingApprovalsData) {
          const answerId = approval.intent_metadata?.answer_id;
          const promptId = approval.intent_metadata?.prompt_id;
          const approvalId = approval.approval_id;
          if (!answerId || !promptId || !approvalId) continue;

          queryClient.setQueryData([QUERYKEYS.CHAT_MESSAGES, chat_id, spaceId], (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              messages: oldData.messages.map((message) => {
                if (message.id !== promptId) return message;
                return {
                  ...message,
                  answers: message.answers.map((ans) => {
                    if (ans.id === answerId) {
                      return { ...ans, awaiting_approval: true, approval_id: approvalId };
                    }
                    return ans;
                  }),
                };
              }),
            };
          });
        }
      }
    } else {
      setPendingApprovals({});
    }
  }, [pendingApprovalsData, chat_id, user?.uid, queryClient, spaceId]);
  
  // Handler for new approval cards received during generation
  const handleApprovalCardReceived = useCallback((cardData) => {
    // Link approval to the answer slot (the orphan with empty content)
    if (queryClient) {
      queryClient.setQueryData([QUERYKEYS.CHAT_MESSAGES, chat_id, spaceId], (oldData) => {
        if (!oldData) return oldData;
        
        return {
          ...oldData,
          messages: oldData.messages.map((message) => {
            if (message.id === cardData.message_id) {
              const answers = message.answers || [];
              
              // Find the last answer (the optimistic empty slot)
              if (answers.length > 0) {
                const updatedAnswers = [...answers];
                const lastIndex = answers.length - 1;
                const lastAnswer = answers[lastIndex];
                
                // Update the orphan answer to link with this approval
                updatedAnswers[lastIndex] = {
                  ...lastAnswer,
                  id: cardData.answer_id, // Use backend's answer_id
                  awaiting_approval: true,
                  approval_id: cardData.approval_id,
                };
                
                return {
                  ...message,
                  answers: updatedAnswers,
                };
              }
            }
            return message;
          }),
        };
      });
    }
    
    setPendingApprovals((prev) => {
      let filtered = prev;
      if (!cardData.approval_group_id) {
        filtered = {};
        for (const [id, data] of Object.entries(prev)) {
          if (data.message_id !== cardData.message_id) {
            filtered[id] = data;
          }
        }
      }
      return {
        ...filtered,
        [cardData.approval_id]: {
          ...cardData,
          status: "pending",
          completedAt: null,
        },
      };
    });
    // Scroll to show the approval card
    setTimeout(() => scrollToBottom(), 100);
  }, [queryClient, chat_id, spaceId]);
  
  // Handler for user confirming approval
  const handleApprovalConfirm = useCallback(async (approvalId, confirmationText) => {
    const approval = pendingApprovalsRef.current[approvalId];
    if (!approval?.answer_id || !approval?.message_id) {
      logger.error('useApprovalFlow', 'Approval identity missing before confirmation', { approvalId, chat_id });
      return;
    }

    // Set status to "processing" immediately for UI feedback
    // Keep card visible to show context during agent execution
    setPendingApprovals((prev) => ({
      ...prev,
      [approvalId]: { ...prev[approvalId], status: "processing" },
    }));
    
    try {
      const approvalResponse = await respondToApproval({
        approvalId,
        action: "approve",
        confirmationText,
        chatId: chat_id,
      });

      if (approvalResponse?.status === "approved_waiting") {
        setPendingApprovals((prev) => ({
          ...prev,
          [approvalId]: {
            ...prev[approvalId],
            status: "approved_waiting",
            processingText: "Approved, waiting for other actions...",
          },
        }));
        queryClient?.invalidateQueries({
          queryKey: [QUERYKEYS.CHAT_MESSAGES, chat_id, spaceId],
        });
        return;
      }

      if (approvalResponse?.status === "already_approved") {
        setPendingApprovals((prev) => removeApprovalGroup(prev, approvalId));
        queryClient?.invalidateQueries({
          queryKey: [QUERYKEYS.CHAT_MESSAGES, chat_id, spaceId],
        });
        queryClient?.invalidateQueries({
          queryKey: [QUERYKEYS.PENDING_APPROVALS, chat_id],
        });
        return;
      }

      const reader = approvalResponse;
      
      // If no reader (shouldn't happen for approve), remove card
      if (!reader) {
        logger.warn('useApprovalFlow', 'No stream reader returned for approve action', { approvalId });
        setPendingApprovals((prev) => removeApprovalGroup(prev, approvalId));
        return;
      }
      
      // Process streaming response from approved agent execution
      if (queryClient) {
        const { runMessageStream, updateGenerateMessage, updateAnswerUsage } = await import('../../utils/streamingUtils');
        
        const promptId = approval.message_id;
        const answerId = approval.answer_id;

        const cachedMessages = queryClient.getQueryData([
          QUERYKEYS.CHAT_MESSAGES,
          chat_id,
          spaceId,
        ]);
        const persistedActivityItems = cachedMessages?.messages
          ?.find((message) => message.id === promptId)
          ?.answers?.find((answer) => answer.id === answerId)
          ?.activity || [];
        
        // Clear awaiting_approval flag from the answer (already has correct answer_id from handleApprovalCardReceived)
        // This allows streaming to fill the existing answer slot
        queryClient.setQueryData([QUERYKEYS.CHAT_MESSAGES, chat_id, spaceId], (oldData) => {
          if (!oldData) return oldData;
          
          return {
            ...oldData,
            messages: oldData.messages.map((message) => {
              if (message.id === promptId) {
                return {
                  ...message,
                  answers: message.answers.map((ans) => {
                    if (ans.id === answerId) {
                      const resumedAnswer = { ...ans };
                      delete resumedAnswer.error_title;
                      delete resumedAnswer.error_code;
                      // Clear approval flags, keep the answer_id from backend
                      return {
                        ...resumedAnswer,
                        awaiting_approval: false,
                        approval_id: undefined,
                        content: '', // Ensure content is empty for streaming
                        status: 'generating',
                        isError: false,
                        timestamp: new Date().toISOString(),
                        role: 'model',
                        model_id: approval?.model_id || null,
                      };
                    }
                    return ans;
                  }),
                };
              }
              return message;
            }),
          };
        });
        
        // Start loading state to show streaming loader
        // Mirrors normal generate flow: loader appears immediately after optimistic update
        if (loadingHandlers?.startLoading) {
          loadingHandlers.startLoading(promptId, null, answerId);
        }
        useStreamingStateStore.getState().setGenerationId(promptId, answerId);
        useGenerateApiSignal.getState().addSignal(chat_id, {
          generate_id: promptId,
          generation_id: answerId,
        });
        
        let firstChunk = false;
        await runMessageStream({
          reader,
          messageId: promptId,
          loadingHandlers,
          buffered: false,
          // Approval streams flow start-to-finish in a single response (no
          // ADK early-ack / pendingGenerations entry to wait on).
          checkStillPending: false,
          preserveActivity: true,
          initialActivityItems: persistedActivityItems,
          onApprovalCard: handleApprovalCardReceived,
          writeChunk: (content) =>
            updateGenerateMessage(queryClient, chat_id, spaceId, promptId, content),
          onUsage: (data) =>
            updateAnswerUsage(queryClient, chat_id, spaceId, promptId, data.answer_id, data),
          onChunkBefore: () => {
            // Remove approval card on first chunk — content is now visible.
            if (!firstChunk) {
              firstChunk = true;
              setPendingApprovals((prev) => removeApprovalGroup(prev, approvalId));
            }
          },
          onCompleteAfter: () => {
            // Invalidate messages cache so the APPROVED activity entry shows up.
            queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chat_id, spaceId] });
          },
          onError: (error) => {
            logger.error('useApprovalFlow', 'Streaming error after approval', { err: error?.message, approvalId, chat_id });
            // Remove card on error
            setPendingApprovals((prev) => removeApprovalGroup(prev, approvalId));
            if (loadingHandlers?.cleanup) {
              loadingHandlers.cleanup();
            }
          },
        });
      } else {
        // No queryClient - just remove card (shouldn't happen)
        logger.warn('useApprovalFlow', 'No queryClient provided, cannot process stream', { approvalId });
        setPendingApprovals((prev) => removeApprovalGroup(prev, approvalId));
      }
      
    } catch (error) {
      logger.error('useApprovalFlow', 'Approval confirmation failed', { err: error?.message, approvalId, chat_id });
      // Reset card to pending state on error (allow retry)
      setPendingApprovals((prev) => ({
        ...prev,
        [approvalId]: { ...prev[approvalId], status: "pending" },
      }));
    }
  }, [chat_id, loadingHandlers, queryClient, spaceId, handleApprovalCardReceived]);
  
  // Handler for user cancelling approval
  const handleApprovalCancel = useCallback(async (approvalId) => {
    const approval = pendingApprovalsRef.current[approvalId];
    // Set status to "processing" with cancel-specific text
    setPendingApprovals((prev) => ({
      ...prev,
      [approvalId]: { ...prev[approvalId], status: "processing", processingText: "Cancelling..." },
    }));
    
    try {
      await respondToApproval({
        approvalId,
        action: "cancel",
        chatId: chat_id,
      });
      
      // Backend has written the denial message — await cache refetch so message is visible before card removal
      if (queryClient) {
        await queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chat_id, spaceId] });
      }
      
      // Remove card — the denial activity message replaces it
      setPendingApprovals((prev) => removeApprovalGroup(prev, approvalId));
      if (approval?.message_id) {
        useStreamingStateStore.getState().failStreaming(approval.message_id);
      }
      // Generation is fully cancelled — clear the sidebar dot.
      loadingHandlers?.stopLoading();
        
    } catch (error) {
      logger.error('useApprovalFlow', 'Approval cancellation failed', { err: error?.message, approvalId, chat_id });
      // Still invalidate + remove — backend may have processed it
      if (queryClient) {
        await queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chat_id, spaceId] });
      }
      setPendingApprovals((prev) => removeApprovalGroup(prev, approvalId));
      if (approval?.message_id) {
        useStreamingStateStore.getState().failStreaming(approval.message_id);
      }
      loadingHandlers?.stopLoading();
    }
  }, [chat_id, loadingHandlers, queryClient, spaceId]);
  
  // Handler for approval expiring (timeout)
  const handleApprovalExpire = useCallback(async (approvalId) => {
    // Only expire if still in pending state (user action takes priority)
    const approval = pendingApprovalsRef.current[approvalId];
    if (approval?.status !== "pending") {
      return; // Don't overwrite if user already clicked confirm/cancel
    }
    
    // Show processing state with expire-specific text
    setPendingApprovals((prev) => ({
      ...prev,
      [approvalId]: { ...prev[approvalId], status: "processing", processingText: "Expiring..." },
    }));
    
    // Mark as expired in backend - this creates the Activity message in Firestore
    await markApprovalExpired(approvalId, chat_id);
    
    // Backend has written the expiry message — await cache refetch so message is visible before card removal
    if (queryClient) {
      await queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chat_id, spaceId] });
    }
    
    // Remove card — the expiry activity message replaces it
    setPendingApprovals((prev) => removeApprovalGroup(prev, approvalId));
    // Generation expired — clear the sidebar dot.
    loadingHandlers?.stopLoading();
  }, [chat_id, loadingHandlers, queryClient, spaceId]);
  
  // Handler for clearing all approvals associated with a message
  const handleClearApprovals = useCallback((messageId) => {
    setPendingApprovals((prev) => {
      const filtered = {};
      let changed = false;
      for (const [id, data] of Object.entries(prev)) {
        if (data.message_id !== messageId) {
          filtered[id] = data;
        } else {
          changed = true;
        }
      }
      return changed ? filtered : prev;
    });
  }, []);
  
  // Handler for clearing all pending approvals (e.g., when user sends new message)
  const handleClearAllApprovals = useCallback(() => {
    setPendingApprovals((prev) => {
      if (Object.keys(prev).length === 0) return prev;
      return {};
    });
  }, []);
  
  return {
    pendingApprovals,
    setPendingApprovals,
    handleApprovalCardReceived,
    handleApprovalConfirm,
    handleApprovalCancel,
    handleApprovalExpire,
    handleClearApprovals,
    handleClearAllApprovals,
  };
};
